import React, { createContext, useContext, useRef, useState } from "react";
import SideBottomModal from "./SideBottomModal/SideBottomModal";
import useFiltersStore from "../../../../../stores/filtersStore";
import { getValidQuantitiesForTicketGroup } from "../../../../../utilities/helpers";

// Create the context
const SecondModalContext = createContext(undefined);

export const useSecondModal = () => {
  const context = useContext(SecondModalContext);
  if (!context) {
    throw new Error("Context must be used within a SecondModalProvider");
  }
  return context;
};

// Provide certain type of modal used in the page
export const SecondModalProvider = ({ children }) => {
  const [selectedFromQuickpicks, setSelectedFromQuickpicks] = useState(null);
  const dialogRef = useRef(null);
  const eventTicketLimit = useFiltersStore((state) => state.eventTicketLimit);

  const changeQuickpickQuantity = (newQuantity, isOfferDiscount, offer) => {
    setSelectedFromQuickpicks((prev) => {
      const quantity =
        typeof newQuantity === "string" ? Number(newQuantity) : newQuantity;

      if (isOfferDiscount && offer) {
        return {
          ...prev,
          offer: {
            ...prev.offer,
            offer_discounts: prev.offer.offer_discounts.map((discount) =>
              discount.id === offer.id ? { ...discount, quantity } : discount,
            ),
          },
        };
      }

      return {
        ...prev,
        quantity,
      };
    });
  };

  const openModal = (pick, quantity) => {
    let normalizedQuantity =
      typeof quantity === "string" ? Number(quantity) : quantity;

    // Resale listings have a fixed quantity
    if (pick?.listing?.quantity) {
      normalizedQuantity = pick.listing.quantity;
    } else if (pick?.offer) {
      const availableCount = pick.GA
        ? pick.availableCount
        : Array.isArray(pick.seatIds)
          ? pick.seatIds.length
          : 0;

      const options = getValidQuantitiesForTicketGroup(
        pick.offer,
        availableCount,
        eventTicketLimit,
      );

      // Clamp to a valid value so the modal never opens with an impossible quantity
      if (!options.includes(normalizedQuantity)) {
        if (options.length === 0) {
          normalizedQuantity = 1;
        } else if (
          typeof normalizedQuantity === "number" &&
          Number.isFinite(normalizedQuantity) &&
          normalizedQuantity > options[options.length - 1]
        ) {
          normalizedQuantity = options[options.length - 1];
        } else {
          normalizedQuantity = options[0];
        }
      }
    }

    setSelectedFromQuickpicks({ ...pick, quantity: normalizedQuantity });
    dialogRef.current.showModal();
  };

  const closeModal = () => {
    dialogRef.current.close();
  };

  return (
    <SecondModalContext.Provider
      value={{
        openModal,
        closeModal,
      }}
    >
      {children}
      <SideBottomModal
        dialogRef={dialogRef}
        handleClose={closeModal}
        selectedFromQuickpicks={selectedFromQuickpicks}
        changeQuickpickQuantity={changeQuickpickQuantity}
      />
    </SecondModalContext.Provider>
  );
};
